import React, { useEffect, useState } from 'react';
import { Bell, BellOff, AlertCircle, Send } from 'lucide-react';
import {
  isPushSupported,
  getSwRegistration,
  getCurrentSubscription,
  subscribeToPush,
  unsubscribeFromPush,
  sendTestPush,
} from '../lib/pushNotifications';

export default function PushNotificationToggle({ compact = false }) {
  const [supported] = useState(isPushSupported());
  const [permission, setPermission] = useState(typeof Notification !== 'undefined' ? Notification.permission : 'default');
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  useEffect(() => {
    if (!supported) return;
    (async () => {
      try {
        await getSwRegistration();
        const sub = await getCurrentSubscription();
        setSubscribed(!!sub);
      } catch (e) { /* ignore */ }
    })();
  }, [supported]);

  const flash = (type, text) => {
    if (type === 'ok') { setMsg(text); setErr(''); }
    else { setErr(text); setMsg(''); }
    setTimeout(() => { setMsg(''); setErr(''); }, 4000);
  };

  const handleSubscribe = async () => {
    setLoading(true);
    try {
      await subscribeToPush();
      setSubscribed(true);
      setPermission(Notification.permission);
      flash('ok', 'Notificari activate ✓');
    } catch (e) {
      flash('err', e.message || 'Eroare la activare');
    } finally { setLoading(false); }
  };

  const handleUnsubscribe = async () => {
    setLoading(true);
    try {
      await unsubscribeFromPush();
      setSubscribed(false);
      flash('ok', 'Notificari dezactivate');
    } catch (e) {
      flash('err', e.message || 'Eroare');
    } finally { setLoading(false); }
  };

  const handleTest = async () => {
    setLoading(true);
    try {
      await sendTestPush();
      flash('ok', 'Notificare test trimisa ✓ (verifica notificarile)');
    } catch (e) {
      flash('err', e.response?.data?.detail || e.message || 'Eroare');
    } finally { setLoading(false); }
  };

  if (!supported) {
    return (
      <div className="card-surface p-4 flex items-center gap-2 text-sm" style={{ color: 'var(--brand-text-secondary)' }} data-testid="push-unsupported">
        <AlertCircle className="w-4 h-4" /> Browser-ul tau nu suporta notificari push.
      </div>
    );
  }

  return (
    <div className={compact ? '' : 'card-surface p-4 sm:p-5 mb-6'} data-testid="push-toggle">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          {subscribed ? <Bell className="w-4 h-4" style={{ color: 'var(--brand-primary)' }} /> : <BellOff className="w-4 h-4" style={{ color: 'var(--brand-text-secondary)' }} />}
          <div className="min-w-0">
            <div className="text-sm font-medium">Notificari push pe acest dispozitiv</div>
            <div className="text-xs" style={{ color: 'var(--brand-text-secondary)' }}>
              {permission === 'denied' ? 'BLOCAT in browser - activeaza din setari site' :
                subscribed ? 'Activate · primesti alerte instant' : 'Activeaza pentru a primi alerte instant'}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {subscribed && (
            <button onClick={handleTest} disabled={loading} className="btn-ghost text-xs px-3 py-1.5 flex items-center gap-1" data-testid="push-test">
              <Send className="w-3 h-3" /> Test
            </button>
          )}
          {subscribed ? (
            <button onClick={handleUnsubscribe} disabled={loading} className="btn-ghost text-xs px-3 py-1.5" data-testid="push-unsubscribe">
              Dezactiveaza
            </button>
          ) : (
            <button onClick={handleSubscribe} disabled={loading || permission === 'denied'} className="btn-gold-filled text-xs px-3 py-1.5" data-testid="push-subscribe">
              {loading ? '...' : 'Activeaza'}
            </button>
          )}
        </div>
      </div>
      {msg && <div className="text-xs mt-3" style={{ color: 'var(--brand-primary)' }}>{msg}</div>}
      {err && <div className="text-xs mt-3" style={{ color: '#fca5a5' }}>{err}</div>}
    </div>
  );
}
